import { getLaneTotal } from "./productionLaneLoading";

export function mergeProductionLaneCards(existingCards = [], incomingCards = []) {
  const seenIds = new Set(
    existingCards
      .map((card) => card?.id)
      .filter((id) => id !== null && id !== undefined && id !== "")
      .map(String)
  );
  const appended = [];

  for (const card of incomingCards) {
    const id = card?.id;
    if (id !== null && id !== undefined && id !== "") {
      const normalizedId = String(id);
      if (seenIds.has(normalizedId)) continue;
      seenIds.add(normalizedId);
    }
    appended.push(card);
  }

  return [...existingCards, ...appended];
}

export function getProductionLanePageState({ page, total, pageSize, hasMore, error = null }) {
  const normalizedPage = Number(page) || 1;
  const normalizedTotal = Number(total) || 0;
  const normalizedPageSize = Number(pageSize) || 1;

  return {
    page: normalizedPage,
    total: normalizedTotal,
    hasMore: typeof hasMore === "boolean" ? hasMore : normalizedPage * normalizedPageSize < normalizedTotal,
    loading: false,
    error,
  };
}
// Refresh the entire loaded window atomically, without exceeding the API's
// 200-card cap. Every chunk uses an aligned offset; the cursor remains in
// normal 25-card pages, not in the temporary refresh chunk size.
export async function fetchProductionLaneRefresh({
  url, laneKey, page = 1, pageSize = 25, request, isCurrent = () => true,
}) {
  const loadedPage = Math.max(1, Math.floor(Number(page) || 1));
  const target = loadedPage * pageSize;
  let offset = 0;
  let result;
  let windowTotal;
  let complete = false;
  const data = [];
  const seen = new Set();
  while (offset < target && !complete) {
    if (!isCurrent()) throw Object.assign(new Error("Stale Production refresh"), { name: "AbortError" });
    let size = Math.min(200, target - offset);
    while (offset % size !== 0) size -= pageSize;
    const chunkPage = offset / size + 1;
    const chunkUrl = new URL(url, window.location.origin);
    chunkUrl.searchParams.set("page", String(chunkPage));
    chunkUrl.searchParams.set("pageSize", String(size));
    // Invalidate once per logical manual refresh, not once per chunk.
    if (offset > 0) chunkUrl.searchParams.delete("refresh");
    result = await request(chunkUrl.toString());
    if (!isCurrent()) throw Object.assign(new Error("Stale Production refresh"), { name: "AbortError" });
    if ((result.page != null && Number(result.page) !== chunkPage) ||
        (result.pageSize != null && Number(result.pageSize) !== size) || !Array.isArray(result.data)) {
      throw new Error("The server returned a different refresh page. Please retry.");
    }
    const total = getLaneTotal(result, laneKey);
    // These offset reads do not share a server snapshot. Do not publish a
    // mixed window/cursor if another user's transition changes its count.
    if (windowTotal !== undefined && total !== windowTotal) {
      throw new Error("Production changed while refreshing. Please retry.");
    }
    windowTotal = total;
    complete = typeof result.hasMore === "boolean" ? !result.hasMore : offset + result.data.length >= total;
    if (result.data.length < size && !complete) {
      throw new Error("Incomplete Production refresh. Please retry.");
    }
    for (const card of result.data) {
      const id = laneKey === "Planned" ? card.external_key : card.step_id ?? card.stepid ?? card.external_key;
      if (id != null && seen.has(String(id))) continue;
      if (id != null) seen.add(String(id));
      data.push(card);
    }
    offset += result.data.length;
  }
  return {
    ...result, data,
    refreshPage: Math.max(1, Math.ceil(offset / pageSize)),
    hasMore: typeof result.hasMore === "boolean" ? result.hasMore : offset < getLaneTotal(result, laneKey),
  };
}
